/**
 * screenTracking.ts
 *
 * User Audit screen views (plan 2026-10-15-001, U6 / R3, KTD10).
 *
 * The app tells the server which screen a staff member opened; the server
 * takes the person and Account from the token and records one row. This
 * module holds everything about that worth testing without React: what an
 * address is called and which record it shows, what text may be sent, when a
 * location counts as settled, and the request itself. useScreenTracking wires
 * it to the router inside Layout, which only renders for signed-in staff.
 */
import { resolvePageName } from '@/components/ai-sidebar/pageNames';

/** A location must stay put this long before it is reported. */
export const SCREEN_SETTLE_MS = 750;

const SCREEN_MAX = 100;
const SECTION_MAX = 64;

/**
 * The server's character set for screen and section text
 * (StaffActivityController::SAFE_TEXT): ASCII letters, digits, space and
 * & › / - ' ( ) , . and never starting with = + - @. Anything else is refused
 * with no row, so the app cleans the text to fit before sending.
 */
export const SAFE_SCREEN_TEXT = /^(?![=+\-@])[A-Za-z0-9 &›/\-'(),.]+$/;

const DISALLOWED = /[^A-Za-z0-9 &›/\-'(),.]/g;
const LEADING_UNSAFE = /^[=+\-@\s]+/;

export type ScreenRecordType = 'patient' | 'plan' | 'lead';

export interface ScreenView {
  screen: string;
  section: string | null;
  recordType: ScreenRecordType | null;
  recordId: number | null;
}

/**
 * Clean text to the server's character set and length, or null when nothing
 * usable is left. The page-name fallback joins a nav label and a path tail
 * with an em dash; that becomes the breadcrumb arrow the set allows.
 */
export function sanitizeScreenText(text: string, max: number): string | null {
  const cleaned = text
    .replace(/\s*[—–]\s*/g, ' › ')
    .replace(DISALLOWED, ' ')
    .replace(/\s+/g, ' ')
    .replace(LEADING_UNSAFE, '')
    .slice(0, max)
    .trim();

  return cleaned === '' ? null : cleaned;
}

/** Most specific first: a plan inside a patient chart is the plan. */
const RECORD_PATTERNS: Array<[RegExp, ScreenRecordType]> = [
  [/^\/patients\/\d+\/plans\/(\d+)\/?$/, 'plan'],
  [/^\/patients\/(\d+)\/?$/, 'patient'],
  [/^\/plans\/(\d+)\/?$/, 'plan'],
  [/^\/leads\/(\d+)\/?$/, 'lead'],
];

function recordFor(pathname: string): { recordType: ScreenRecordType; recordId: number } | null {
  for (const [pattern, recordType] of RECORD_PATTERNS) {
    const match = pattern.exec(pathname);
    if (!match) continue;
    const recordId = Number(match[1]);
    if (Number.isSafeInteger(recordId) && recordId > 0) {
      return { recordType, recordId };
    }
  }
  return null;
}

function sectionFor(search: string): string | null {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search).get('section');
  } catch {
    raw = null;
  }
  return raw ? sanitizeScreenText(raw, SECTION_MAX) : null;
}

/** What the server is told about this address, or null if it has no name. */
export function describeScreen(pathname: string, search: string): ScreenView | null {
  const screen = sanitizeScreenText(resolvePageName(pathname), SCREEN_MAX);
  if (screen === null) return null;

  const record = recordFor(pathname);
  return {
    screen,
    section: sectionFor(search),
    recordType: record?.recordType ?? null,
    recordId: record?.recordId ?? null,
  };
}

export interface ScreenTrackerOptions {
  send: (view: ScreenView) => void;
  /** False while there is no session or one is ending; checked at send time. */
  isActive: () => boolean;
  settleMs?: number;
}

export interface ScreenTracker {
  onLocation: (pathname: string, search: string) => void;
  dispose: () => void;
}

/**
 * Report a location once it has been stable for `settleMs`, so a redirect
 * reports only where it ended up, and skip a location whose path and section
 * match the last one reported. Other query parameters (paging, filters) do
 * not make a new screen.
 */
export function createScreenTracker({
  send,
  isActive,
  settleMs = SCREEN_SETTLE_MS,
}: ScreenTrackerOptions): ScreenTracker {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastKey: string | null = null;

  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    onLocation(pathname, search) {
      cancel();
      timer = setTimeout(() => {
        timer = null;
        if (!isActive()) return;
        const view = describeScreen(pathname, search);
        if (view === null) return;
        const key = `${pathname}|${view.section ?? ''}`;
        if (key === lastKey) return;
        lastKey = key;
        send(view);
      }, settleMs);
    },
    dispose: cancel,
  };
}

/**
 * Send one screen view. A raw keepalive `fetch`, like revokeServerSide in
 * lib/session.ts, and never apiClient: apiClient signs the user out on a 401,
 * and a report that failed must never end a session or move anyone. Every
 * failure (network, 401, 429, a browser without keepalive) is swallowed.
 */
export function sendScreenView(
  view: ScreenView,
  auth: { token: string; accountId: number | null },
): void {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v5';
  const headers: Record<string, string> = {
    Authorization: `Bearer ${auth.token}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (auth.accountId) {
    headers['X-Target-Company'] = String(auth.accountId);
  }

  const body: Record<string, string | number> = { screen: view.screen };
  if (view.section !== null) body.section = view.section;
  if (view.recordType !== null && view.recordId !== null) {
    body.recordType = view.recordType;
    body.recordId = view.recordId;
  }

  try {
    void fetch(`${base}/activity/screen`, {
      method: 'POST',
      keepalive: true,
      headers,
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    // Nothing to do: an unrecorded screen view never affects the person.
  }
}
